import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BButton, BCard, BFormGroup, BFormInput } from "@/lib/typed-bootstrap";

import { IEnumState } from "@/models";
import { removeSpecialCharacters } from "@/utils";
import _debounce from "lodash/debounce";

import FormEnumValue from "./FormEnumValue.component";

import plusIcon from "@/assets/img/plus-icon-thin-light.svg";

import trashIconRed from "@/assets/img/icon-trash-red.svg";
import {
  Button,
  ButtonShapeEnum,
  ButtonSizeEnum,
  ButtonThemeEnum,
} from "../shared/Button.component";
import styles from "./FormEnumWidget.component.module.scss";

export interface IEnumNameBlurEvent {
  oldName: string;
  newName: string;
}

@Component
export default class FormEnumWidget extends Vue {
  _tsx!: tsx.DeclareProps<{
    value: FormEnumWidget["value"];
    isUnique?: boolean | null;
  }> &
    tsx.DeclareOnEvents<{
      onEnumNameChange: IEnumNameBlurEvent;
      onAddEnumTypeValue: void;
      onInput: IEnumState;
      onDelete: void;
    }>;

  $refs!: {
    enumNameInput: HTMLFormElement;
  };

  @Prop({ required: true }) value!: IEnumState;

  selectedValueAction: string | null = null;
  actionOptions = [
    { value: "delete", text: this.$t("global_cta_delete").toString() },
  ];

  get noValueSelected(): boolean {
    return !this.value.values.some((val) => val.selected);
  }

  get debouncedNameChangeEmitter() {
    return _debounce((newName: string, oldName: string) => {
      this.onEnumNameChange({ oldName, newName });
    }, 300);
  }

  @Emit("enumNameChange") onEnumNameChange(_event: IEnumNameBlurEvent) {}
  @Emit("input") onInput(_event: IEnumState) {}

  mounted() {
    if (!this.value?.name) {
      this.$refs.enumNameInput?.focus();
    }
  }

  @Watch("value.name")
  onNameChanged(newName: string, oldName: string) {
    this.debouncedNameChangeEmitter(newName, oldName);
  }

  handleDelete(index: number) {
    this.value.values.splice(index, 1);
  }

  inputFieldFormatter(value: string): string {
    return removeSpecialCharacters(value);
  }

  isUniqueValue(valueName: string, valueIndex: number): boolean | null {
    const isDuplicate = this.value.values.some((val, index) => {
      return index !== valueIndex && val.name === valueName;
    });
    return isDuplicate ? false : null;
  }

  onAddValue() {
    try {
      const lastValue = this.value.values[this.value.values.length - 1];
      if (!lastValue || lastValue.name.length > 0) {
        this.value.values.push({ name: "", selected: false });
      }
    } catch (err) {
      console.error(err);
    }
  }

  deleteEnumType() {
    this.$emit("delete", this.value.name);
  }

  render() {
    return (
      <BCard bodyClass={styles.body} class={styles.card}>
        <div class={styles.header}>
          <div class={styles.sectionHeader}>{this.$t("enum_widget_name")}</div>
          <Button
            class={styles.deleteButton}
            shape={ButtonShapeEnum.square}
            theme={ButtonThemeEnum.naked}
            size={ButtonSizeEnum.sm}
            onClick={this.deleteEnumType}
          >
            <img src={trashIconRed} class={styles.icon} />
            {this.$t("api_details_data_delete_enum")}
          </Button>
        </div>

        <BFormGroup class="object-field__form-group">
          <BFormInput
            type="text"
            class="default__form-input"
            placeholder={this.$t("enum_widget_name_placeholder").toString()}
            value={this.value.name}
            onInput={(val: string | number) =>
              (this.value.name = val.toString())
            }
            maxLength={64}
            ref="enumNameInput"
            required
            formatter={this.inputFieldFormatter}
            trim
          />
        </BFormGroup>

        <div class={styles.sectionHeader}>{this.$t("enum_widget_values")}</div>
        <div class="section-header__underline"></div>

        <div class="ml-0">
          {this.value.values.map((enumValue, index) => (
            <FormEnumValue
              key={`enum-value-${index}`}
              value={enumValue}
              isUnique={this.isUniqueValue(enumValue.name, index)}
              onInput={(newValue) => {
                this.value.values[index].name = newValue.name;
              }}
              onDelete={() => this.handleDelete(index)}
            />
          ))}
        </div>

        <BButton
          class="create__button--shallow mt-2"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            this.onAddValue();
          }}
        >
          <img src={plusIcon} class={styles.icon} />
          <div class="button__icon-text">
            {this.$t("enum_widget_add_value")}
          </div>
        </BButton>
      </BCard>
    );
  }
}
